// src/components/tasks/TaskList.js
// ✅ DROP-IN: Compact modern list (no yellow sticky notes)
// ✅ ADDED: Client-side filtering for task permissions
// ✅ ADDED: Attachment icon + count (non-intrusive; click opens edit form)
// Columns: Name | Due | Status | Assign
// Row click edits, inline status dropdown, search + filter + sort.

import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Edit2,
  Trash2,
  Users,
  CalendarDays,
  Flag,
  Paperclip,
  Plus,
  ShieldAlert,
  Clock,
  ChevronDown,
  Download,
} from "lucide-react";
import { TaskService } from "./TaskService";
import { filterTasksForUser } from "../../utils/taskFilters";
import * as XLSX from "xlsx";
import SurfaceState from "../common/SurfaceState";

const STATUS_OPTIONS = ["To Do", "In Progress", "Completed", "On Hold", "Cancelled"];

const STATUS_STYLES = {
  "To Do": "bg-slate-100 text-slate-700 border-slate-200",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "On Hold": "bg-amber-50 text-amber-800 border-amber-200",
  Cancelled: "bg-rose-50 text-rose-700 border-rose-200",
};

const PRIORITY_DOT = {
  Low: "bg-slate-300",
  Medium: "bg-blue-400",
  High: "bg-amber-400",
  Critical: "bg-rose-500",
};

function displayUser(u) {
  return u?.fullName || u?.name || u?.displayName || u?.email || u?.id || "User";
}

function formatDueShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function isOverdue(task) {
  if (!task?.dueAt) return false;
  if ((task.status || "To Do") === "Completed") return false;
  const d = new Date(task.dueAt);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < Date.now();
}

export default function TaskList({
  companyId,
  me,
  usersMap,
  groupsMap,
  onEdit,
  onRefresh,
  onCreateTask,
  showManagerReport = false,
}) {
  const [rawTasks, setRawTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [sortBy, setSortBy] = useState("dueAsc"); // dueAsc | dueDesc | titleAsc
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ✅ CLIENT-SIDE FILTERING: Only show tasks user has permission to see
  const tasks = useMemo(() => {
    return filterTasksForUser(rawTasks, me);
  }, [rawTasks, me]);

  const load = async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError("");
      const all = await TaskService.getCompanyTasks(companyId, { me });
      setRawTasks(Array.isArray(all) ? all : []);
    } catch (e) {
      console.error("[TaskList] load error:", e);
      setError("Failed to load tasks.");
      setRawTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, me?.uid, me?.id]);

  useEffect(() => {
    if (!showActionsMenu) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (target?.closest?.("[data-task-actions-wrap]")) return;
      setShowActionsMenu(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowActionsMenu(false);
    };
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showActionsMenu]);


  const assigneesLabel = (task) => {
    if (task.assignMode === "group") {
      const g =
        task.groupName ||
        groupsMap?.[task.groupId]?.name ||
        groupsMap?.[task.groupId]?.title ||
        "";
      return g ? `Group: ${g}` : "Group task";
    }
    const ids = Array.isArray(task.assignedToUserIds) ? task.assignedToUserIds : [];
    if (!ids.length) return "Unassigned";
    return ids.map((uid) => displayUser(usersMap?.[uid]) || uid).join(", ");
  };

  const handleStatusChange = async (task, newStatus) => {
    if (!task?.id) return;
    setBusyId(task.id);
    setError("");
    try {
      await TaskService.updateTask(companyId, task.id, { status: newStatus });
      await load();
      onRefresh?.();
    } catch (e) {
      console.error("[TaskList] status update failed:", e);
      setError("Failed to update status.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (taskId) => {
    const ok = window.confirm("Delete this task?");
    if (!ok) return;

    setBusyId(taskId);
    setError("");
    try {
      await TaskService.deleteTask(companyId, taskId);
      await load();
      onRefresh?.();
    } catch (e) {
      console.error("[TaskList] delete failed:", e);
      setError("Failed to delete task.");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    const queryText = (q || "").toLowerCase().trim();

    let list = [...tasks];

    if (queryText) {
      list = list.filter((t) => {
        const title = (t.title || "").toLowerCase();
        const desc = (t.description || "").toLowerCase();
        const status = (t.status || "").toLowerCase();
        const pr = (t.priority || "").toLowerCase();
        const ass = assigneesLabel(t).toLowerCase();
        return (
          title.includes(queryText) ||
          desc.includes(queryText) ||
          status.includes(queryText) ||
          pr.includes(queryText) ||
          ass.includes(queryText)
        );
      });
    }

    if (statusFilter === "Active") {
      list = list.filter((t) => {
        const status = t.status || "To Do";
        return status === "To Do" || status === "In Progress";
      });
    } else if (statusFilter !== "All") {
      list = list.filter((t) => (t.status || "To Do") === statusFilter);
    }

    list.sort((a, b) => {
      if (sortBy === "titleAsc") {
        return (a.title || "").localeCompare(b.title || "");
      }

      const ad = a.dueAt ? new Date(a.dueAt).getTime() : Number.POSITIVE_INFINITY;
      const bd = b.dueAt ? new Date(b.dueAt).getTime() : Number.POSITIVE_INFINITY;

      if (sortBy === "dueDesc") return bd - ad;
      return ad - bd; // dueAsc default
    });

    return list;
  }, [tasks, q, statusFilter, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportTasks = async () => {
    try {
      setExporting(true);
      const rows = filtered.map((task) => ({
        Title: task?.title || "",
        Type: task?.type || "",
        Status: task?.status || "To Do",
        Priority: task?.priority || "",
        "Due Date": task?.dueAt ? formatDueShort(task.dueAt) : "",
        Assignees: assigneesLabel(task),
        Account: task?.accountName || task?.accountId || "",
        Description: task?.description || "",
      }));
      const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Note: "No visible tasks to export." }]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Tasks");
      XLSX.writeFile(wb, "TasksExport.xlsx");
    } finally {
      setExporting(false);
    }
  };

  const isAdminOrOwner = ["admin", "owner"].includes(String(me?.role || "").toLowerCase());
  const managerReport = useMemo(() => {
    if (!showManagerReport || !isAdminOrOwner) return null;

    const now = Date.now();
    const overdueTasks = tasks.filter((task) => isOverdue(task));
    const staleOverdue = overdueTasks.filter((task) => {
      const updated = task?.updatedAt?.seconds
        ? task.updatedAt.seconds * 1000
        : task?.updatedAt
        ? new Date(task.updatedAt).getTime()
        : 0;
      return !updated || now - updated >= 7 * 86400000;
    });

    return {
      total: tasks.length,
      overdue: overdueTasks.length,
      stale: staleOverdue.length,
      managerDone: tasks.filter((task) => task?.managerMarkedDone === true).length,
      overdueTasks: overdueTasks.slice(0, 8),
    };
  }, [showManagerReport, isAdminOrOwner, tasks]);

  if (loading) {
    return (
      <div className="py-6">
        <SurfaceState
          tone="loading"
          title="Loading tasks"
          description="Pulling in your current work, due dates, and assignments."
        />
      </div>
    );
  }

  if (error) {
    return (
      <SurfaceState
        tone="error"
        title="Tasks unavailable"
        description={error}
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2 w-full lg:w-auto">
          <div className="relative w-full lg:w-[360px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tasks…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {onCreateTask ? (
            <button
              type="button"
              onClick={onCreateTask}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 md:hidden"
            >
              <Plus className="w-4 h-4" />
              New Task
            </button>
          ) : null}

          <div className="hidden sm:flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
              title="Filter by status"
            >
              <option value="Active">Active</option>
              <option value="All">All</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 text-sm rounded-xl border border-slate-200 bg-white"
              title="Sort"
            >
              <option value="dueAsc">Due date (soonest)</option>
              <option value="dueDesc">Due date (latest)</option>
              <option value="titleAsc">Title (A–Z)</option>
            </select>

            <div className="relative" data-task-actions-wrap>
              <button
                type="button"
                onClick={() => setShowActionsMenu((prev) => !prev)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Actions
                <ChevronDown className="w-4 h-4" />
              </button>

              {showActionsMenu ? (
                <div className="absolute right-0 top-full z-20 mt-2 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                  <button
                    type="button"
                    onClick={async () => {
                      setShowActionsMenu(false);
                      await exportTasks();
                    }}
                    disabled={exporting}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Download className="w-4 h-4 text-slate-500" />
                    {exporting ? "Exporting…" : "Export Tasks"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-500">
          Showing <span className="font-semibold text-slate-700">{filtered.length}</span>{" "}
          {filtered.length === 1 ? "task" : "tasks"}
        </div>
      </div>

      {managerReport ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900">
              <ShieldAlert className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Manager Report</div>
              <div className="text-[11px] text-slate-500">Overdue and stale work snapshot</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            <div className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">Total {managerReport.total}</div>
            <div className="rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-red-700">Overdue {managerReport.overdue}</div>
            <div className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-800">Stale {managerReport.stale}</div>
            <div className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">Mgr done {managerReport.managerDone}</div>
          </div>
          {managerReport.overdueTasks.length ? (
            <div className="mt-3 space-y-2">
              {managerReport.overdueTasks.map((task) => (
                <button
                  key={task.id}
                  type="button"
                  onClick={() => onEdit?.(task)}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2 text-left hover:bg-slate-50"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-900">{task.title || "Untitled Task"}</div>
                    <div className="truncate text-[11px] text-slate-500">{assigneesLabel(task)}</div>
                  </div>
                  <div className="inline-flex items-center gap-1 text-[11px] text-red-700">
                    <Clock className="w-3.5 h-3.5" />
                    Overdue
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Table */}
      <div className="overflow-hidden bg-transparent">
        <div className="hidden md:grid grid-cols-[minmax(0,1fr)_150px_150px_220px_72px] gap-3 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          <div>Name</div>
          <div className="hidden md:block">Due Date</div>
          <div className="hidden md:block">Status</div>
          <div className="hidden lg:block">Assign</div>
          <div className="text-right"> </div>
        </div>

        {filtered.length === 0 ? (
          <div className="px-1 py-4">
            <SurfaceState
              tone="empty"
              title={tasks.length === 0 ? "No tasks yet" : "No tasks match your filters"}
              description={
                tasks.length === 0
                  ? "Create your first task or follow-up and it will show up here."
                  : "Try clearing a filter or switching back to Active to see current work."
              }
            />
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((t) => {
              const status = t.status || "To Do";
              const due = formatDueShort(t.dueAt);
              const overdue = isOverdue(t);
              const assign = assigneesLabel(t);
              const dot = PRIORITY_DOT[t.priority] || "bg-slate-300";

              const attCount = Number(
                t.attachmentsCount ??
                  t.attachmentCount ??
                  t.attachments_count ??
                  (Array.isArray(t.attachments) ? t.attachments.length : 0) ??
                  0
              );

              return (
                <div
                  key={t.id}
                  className="grid grid-cols-[minmax(0,1fr)_72px] gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2 transition cursor-pointer hover:border-slate-200 hover:bg-slate-50 md:grid-cols-[minmax(0,1fr)_150px_150px_220px_72px] md:gap-3 md:px-3.5 md:py-2.5"
                  onClick={() => onEdit?.(t)}
                >
                  {/* Name */}
                  <div className="min-w-0">
                    <div className="flex items-start gap-2 min-w-0">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dot}`} title={t.priority || "Priority"} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {t.title || "Untitled Task"}
                        </div>
                        {t.description ? (
                          <div className="text-xs text-slate-500 truncate">{t.description}</div>
                        ) : null}

                        {/* Sub-meta */}
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                          {t.type ? (
                            <span className="inline-flex items-center gap-1">
                              <Flag className="w-3 h-3" />
                              {t.type}
                            </span>
                          ) : null}

                          {t.distribution === "joint" ? (
                            <span className="px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600">
                              Shared
                            </span>
                          ) : null}

                          {/* ✅ Attachment icon + count (non-intrusive; click opens edit form) */}
                          {attCount > 0 ? (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                              title="View attachments (opens edit)"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.(t);
                              }}
                            >
                              <Paperclip className="w-3 h-3 text-slate-500" />
                              <span className="font-medium">{attCount}</span>
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Due */}
                  <div className="hidden md:flex items-center gap-2 text-sm text-slate-700">
                    <CalendarDays className="w-4 h-4 text-slate-400" />
                    <span className={overdue ? "text-rose-700 font-semibold" : ""}>
                      {due || "—"}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="hidden md:block" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={status}
                      disabled={busyId === t.id}
                      onChange={(e) => handleStatusChange(t, e.target.value)}
                      className={`w-full px-2.5 py-1.5 rounded-xl border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        STATUS_STYLES[status] || STATUS_STYLES["To Do"]
                      }`}
                      title="Change status"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Assign */}
                  <div className="hidden lg:flex items-center gap-2 min-w-0 text-sm text-slate-700">
                    <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="truncate">{assign}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-start justify-end gap-1 md:items-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => onEdit?.(t)}
                      className="p-2 rounded-lg hover:bg-white border border-transparent hover:border-slate-200 transition"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4 text-slate-600" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(t.id)}
                      className="p-2 rounded-lg hover:bg-white border border-transparent hover:border-slate-200 transition"
                      title="Delete"
                      disabled={busyId === t.id}
                    >
                      <Trash2 className="w-4 h-4 text-rose-600" />
                    </button>
                  </div>

                  {/* Mobile-only status + assign */}
                  <div className="col-span-2 flex flex-wrap items-center gap-2 pt-1 text-[11px] text-slate-500 md:hidden" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                        STATUS_STYLES[status] || STATUS_STYLES["To Do"]
                      }`}>
                        {status}
                      </span>
                      <span>{due || "No due date"}</span>
                    </div>
                    <span className="truncate">Assigned: {assign}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
